'use client';

import { useMemo } from 'react';
import { captionStyleToCSS, CAPTION_STYLES, type CaptionStyleName } from '@/lib/utils/caption-styles';

interface Word {
  id: number;
  text: string;
  start: number;
  end: number;
}

interface CaptionEdit {
  wordId: number;
  originalText: string;
  replacementText: string;
}

interface EditPlan {
  cuts?: Array<{ start: number; end: number; wordIds?: number[] }>;
  captions?: CaptionEdit[];
}

interface StyledCaptionOverlayProps {
  currentTime: number;
  transcript: Word[];
  captionStyle: CaptionStyleName | string;
  editPlan?: EditPlan;
  className?: string;
}

/**
 * Styled Caption Overlay Component
 *
 * Displays CSS-approximated captions during video preview with user-selected styling.
 * This provides instant feedback on how captions will look in the final Shotstack export.
 *
 * Features:
 * - Applies caption text replacements from EditPlan
 * - Shows styled captions using CSS (approximates Shotstack output)
 * - Updates in real-time during video playback
 * - Supports all 4 caption style presets (Modern, Classic, Bold, Minimal)
 */
export function StyledCaptionOverlay({
  currentTime,
  transcript,
  captionStyle,
  editPlan,
  className = ''
}: StyledCaptionOverlayProps) {
  // Get current caption text at this playback time
  const currentCaption = useMemo(() => {
    return getCurrentCaptionText(currentTime, transcript, editPlan);
  }, [currentTime, transcript, editPlan]);

  // Get style configuration
  const styleConfig = CAPTION_STYLES[captionStyle as CaptionStyleName] || CAPTION_STYLES.modern;
  const cssStyle = captionStyleToCSS(styleConfig);

  // Don't render if no caption text at current time
  if (!currentCaption || currentCaption.trim().length === 0) {
    return null;
  }

  return (
    <div
      className={`absolute bottom-20 left-1/2 -translate-x-1/2 z-20 pointer-events-none ${className}`}
      style={{ maxWidth: '90%' }}
    >
      <div
        className="caption-overlay-text"
        style={cssStyle}
      >
        {currentCaption}
      </div>
    </div>
  );
}

/**
 * Get caption text at current time with edits applied
 * Handles:
 * - Word-level timestamp matching
 * - Caption text replacements from EditPlan
 * - Multiple words forming a caption sentence
 */
function getCurrentCaptionText(
  currentTime: number,
  transcript: Word[],
  editPlan?: EditPlan
): string {
  if (!transcript || transcript.length === 0) {
    return '';
  }

  // Find words active at current time (with small buffer for smooth transitions)
  const buffer = 0.1; // 100ms buffer
  const activeWords = transcript.filter(word =>
    currentTime >= (word.start - buffer) && currentTime <= (word.end + buffer)
  );

  if (activeWords.length === 0) {
    return '';
  }

  // Apply caption text replacements
  const captionWords = activeWords.map(word => {
    // Check if this word has a caption edit
    const captionEdit = editPlan?.captions?.find(edit => edit.wordId === word.id);

    if (captionEdit) {
      return captionEdit.replacementText;
    }

    return word.text;
  });

  // Join words into caption text
  return captionWords.filter(w => w.length > 0).join(' ');
}

/**
 * Lightweight version for minimal overlays
 */
export function MinimalCaptionOverlay({
  text,
  captionStyle = 'modern'
}: {
  text: string;
  captionStyle?: CaptionStyleName | string;
}) {
  const styleConfig = CAPTION_STYLES[captionStyle as CaptionStyleName] || CAPTION_STYLES.modern;
  const cssStyle = captionStyleToCSS(styleConfig);

  if (!text) return null;

  return (
    <div
      className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
      style={{ maxWidth: '85%' }}
    >
      <div style={cssStyle}>{text}</div>
    </div>
  );
}
